import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CheckIcon from '@material-ui/icons/Check';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

/*
 * Slides 5-8 label the first two steps "Customization" and "Framework".
 *
 * The two blue boxes on those slides are not new controls — each has an arrow
 * running up to a step in this timeline, which is what they rename. "Topic" and
 * "Duration" also stopped being true once the engine took over: the person no
 * longer picks a duration at all (every session is generated at 30-45 minutes),
 * and the first step now gathers the whole shape of the program, not just its
 * topic. The names describe what the step is for rather than one field it used
 * to contain.
 *
 * Order and count are unchanged, so every index into this list still points at
 * the same stage.
 */
const TIMELINE_STAGES = ['Customization', 'Framework', 'Building', 'Preview'];
const ORANGE = '#FF6900';

const ProgramHeaderBanner = ({
    overallProgress = 0,
    timelineActiveIndex = 0,
    actions,
    leftActions,
    showProgress = true,
    isCollapsed = false,
    onToggleCollapse,
}) => {
    const effectiveTimelineIndex = (timelineActiveIndex === 2 && overallProgress >= 100)
        ? 3
        : timelineActiveIndex;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: '8px', md: '24px' },
            height: isCollapsed ? '44px' : { xs: 'auto', md: '90px' },
            minHeight: isCollapsed ? '44px' : { xs: '56px', md: '90px' },
            borderBottom: '1px solid var(--brand-border, #EBEBEB)',
            backgroundColor: 'var(--brand-surface, #fff)',
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            transition: 'height 0.25s ease',
            overflow: 'hidden',
            gap: { xs: '4px', md: '0' },
            py: isCollapsed ? 0 : { xs: '8px', md: 0 },
        }}>
            {/* Left: collapse toggle + optional left actions */}
            <Box sx={{ flex: { xs: '0 0 auto', md: 1 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                {onToggleCollapse && (
                    <IconButton
                        size="small"
                        onClick={onToggleCollapse}
                        sx={{ color: '#999', '&:hover': { color: ORANGE } }}
                    >
                        {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                    </IconButton>
                )}
                {leftActions}
            </Box>

            {/* Centre: step indicator */}
            {showProgress && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0, py: isCollapsed ? 0 : { xs: '4px', md: '15px' } }}>
                    {TIMELINE_STAGES.map((label, i) => {
                        const isDone = i < effectiveTimelineIndex;
                        const isCurrent = i === effectiveTimelineIndex;
                        const isUpcoming = i > effectiveTimelineIndex;
                        const szMd = isCollapsed ? 28 : 40;
                        const szXs = isCollapsed ? 22 : 26;
                        return (
                            <React.Fragment key={label}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', width: { xs: '28px', md: '40px' }, flexShrink: 0 }}>
                                    {/* Circle */}
                                    <Box sx={{
                                        width: { xs: szXs, md: szMd },
                                        height: { xs: szXs, md: szMd },
                                        borderRadius: '50%', flexShrink: 0,
                                        background: isDone ? ORANGE : 'var(--brand-surface, #fff)',
                                        border: `2px solid ${isDone || isCurrent ? ORANGE : '#D1D5DB'}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.25s ease',
                                    }}>
                                        {isDone && <CheckIcon style={{ color: '#fff', fontSize: isCollapsed ? 12 : 14 }} />}
                                    </Box>
                                    {/* Label — hidden when collapsed or on xs */}
                                    {!isCollapsed && (
                                        <Typography sx={{
                                            fontSize: { xs: 9, md: 12 },
                                            fontWeight: isDone || isCurrent ? 700 : 500,
                                            color: isDone || isCurrent ? 'var(--brand-text, #1a1a1a)' : 'var(--brand-text-muted, #9CA3AF)',
                                            lineHeight: 1,
                                            display: { xs: 'none', sm: 'block' },
                                        }}>
                                            {label}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Connecting line */}
                                {i < TIMELINE_STAGES.length - 1 && (
                                    <Box sx={{
                                        width: { xs: isCollapsed ? 16 : 20, md: isCollapsed ? 32 : 48 },
                                        height: 2,
                                        mt: { xs: isCollapsed ? '10px' : '12px', md: isCollapsed ? '13px' : '19px' },
                                        background: i < effectiveTimelineIndex ? ORANGE : 'var(--brand-border, #E5E7EB)',
                                        transition: 'all 0.25s ease',
                                        flexShrink: 0,
                                    }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </Box>
            )}

            {/* Right: actions */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: { xs: '6px', md: '12px' }, justifyContent: 'flex-end' }}>
                {actions}
            </Box>
        </Box>
    );
};

export default ProgramHeaderBanner;
